/**
 * Application Routes Configuration
 * Centralizes all route definitions for better maintainability
 * @module routes
 */

import { Route, Routes } from 'react-router-dom';
import Home from './Pages/Home';
import About from './Pages/About';
import Contact from './Pages/Contact';
import Deliverables from './Pages/Deliverables';
import Terms from './Pages/Terms';
import SimulationsIntro from './Pages/Simulations/Intro';
import { HeatPumpSimulation } from './Pages/Simulations/HeatPumpSimulation';
import { HydraulicBalancingSimulation } from './Pages/Simulations/HydraulicBalancing';
import { HydraulicBalancingTestingSimulation } from './Pages/Simulations/HydraulicBalancingTesting';
import { HybridSystemIntegrationTool } from './Pages/Simulations/HybridSystemIntegration';
import TrainingCurriculumIntro from './Pages/AZCourse/Intro';
import NavigationAssistantReview from './Pages/NavigationAssistantReview';
import NotFound from './Pages/NotFound';
import Login from './Pages/Login';
import Register from './Pages/Register';
import ConfirmEmail from './Pages/ConfirmEmail';
import ForgotPassword from './Pages/ForgotPassword';
import ResetPassword from './Pages/ResetPassword';
import SetPassword from './Pages/SetPassword';
import Profile from './Pages/Profile';
import ProtectedRoute from './components/ProtectedRoute';
import { AppRoles } from './services/authService';
import { MenuGroups } from './constants/menuGroups';

// Admin Pages
import AdminCountries from './Pages/Admin/Countries';
import TrainingCurriculumAdmin from './Pages/Admin/TrainingCurriculum';
import AdminLevelsOfDifficulty from './Pages/Admin/LevelsOfDifficulty';
import AdminExceptionLogs from './Pages/Admin/ExceptionLogs';
import AdminDeletedFiles from './Pages/Admin/DeletedFiles';
import AdminFileStorage from './Pages/Admin/FileStorage';
import AdminDataDeletionLogs from './Pages/Admin/DataDeletionLogs';
import AdminUsers from './Pages/Admin/Users';

// Survey Pages (Content Creator)
import SurveysList from './Pages/Survey/SurveysList';
import SurveyEditor from './Pages/Survey/SurveyEditor';
import SurveyStatistics from './Pages/Survey/SurveyStatistics';
import SurveyResponses from './Pages/Survey/SurveyResponses';
import SurveyParticipate from './Pages/Survey';

// Quiz Pages
import { QuizCollectionsList, QuizCollectionEditor, QuizPlay, QuizStatistics, QuizAttempts } from './Pages/Quiz';

// Event Pages
import { EventsList, EventEditor } from './Pages/Admin/Events';
import { EventsPublic, EventDetail, EventRegistration } from './Pages/Public/Events';

// News Pages
import { NewsList, NewsEditor } from './Pages/Admin/News';
import { NewsPublic, NewsDetail } from './Pages/Public/News';

// Training Profile Pages
import { TrainingProfilesList, TrainingProfileEditor } from './Pages/TrainingProfiles';

// Learning Path Pages
import { LearningPathsList, LearningPathEditor, LearningPathPlay, LearningPathStatistics } from './Pages/LearningPaths';

// Learning Package Pages
import { LearningPackagesList, LearningPackageEditor, LearningPackageStatistics, AnonymousRosterPage } from './Pages/LearningPackages';
import { PackagesPublicList, PackagePublicDetail } from './Pages/Public/LearningPackages';

// Re-export MenuGroups for convenience
export { MenuGroups } from './constants/menuGroups';

/**
 * Route configuration array
 * Each route object contains path, element, and optional metadata
 * @property {string} path - Route path
 * @property {JSX.Element} element - Component to render
 * @property {string} name - Route display name
 * @property {string} description - Route description
 * @property {string[]} [roles] - Required roles (null/undefined = public)
 * @property {string} [menuGroup] - Menu group for navigation (null = not in menu)
 * @property {string} [menuKey] - Translation key for menu label (defaults to name if not set)
 * @property {boolean} [requiresAuth] - Requires authentication but no specific role
 */
export const routes = [
  // Public routes (no auth required)
  {
    path: '/',
    element: <Home />,
    name: 'Home',
    description: 'Application home page',
  },
  {
    path: '/login',
    element: <Login />,
    name: 'Login',
    description: 'User login page',
  },
  {
    path: '/register',
    element: <Register />,
    name: 'Register',
    description: 'New user registration page',
  },
  {
    path: '/confirm-email',
    element: <ConfirmEmail />,
    name: 'Confirm Email',
    description: 'Email confirmation page',
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
    name: 'Forgot Password',
    description: 'Request password reset link',
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
    name: 'Reset Password',
    description: 'Reset password with token from email',
  },
  {
    path: '/set-password',
    element: <SetPassword />,
    name: 'Set Password',
    description: 'Set initial password after account approval',
  },
  // Public menu items
  {
    path: '/about',
    element: <About />,
    name: 'About',
    description: 'About the HeatCraftHP project',
    menuGroup: MenuGroups.PUBLIC,
    menuKey: 'about',
  },
  {
    path: '/contact',
    element: <Contact />,
    name: 'Contact',
    description: 'Contact form for reaching out to HeatCraftHP team',
    menuGroup: MenuGroups.PUBLIC,
    menuKey: 'contact',
  },
  {
    path: '/deliverables',
    element: <Deliverables />,
    name: 'Deliverables',
    description: 'Chronological overview of key HeatCraftHP project deliverables',
    menuGroup: MenuGroups.PUBLIC,
    menuKey: 'deliverables',
  },
  {
    path: '/terms',
    element: <Terms />,
    name: 'Terms & Conditions',
    description: 'Terms and Conditions for using the HeatCraftHP platform',
    menuGroup: MenuGroups.PUBLIC,
    menuKey: 'terms',
  },
  {
    path: '/simulations',
    element: <SimulationsIntro />,
    name: 'Simulations',
    description: 'Interactive simulation tools for learning heat pump technology',
    menuGroup: MenuGroups.PUBLIC,
    menuKey: 'simulations',
  },
  {
    path: '/simulations/heat-pump-simulation',
    element: <HeatPumpSimulation />,
    name: 'Heat Pump Simulation',
    description: 'Interactive heat pump heating system simulator',
  },
  {
    path: '/simulations/heat-pump-training',
    element: <HeatPumpSimulation trainingProfileMode />,
    name: 'Heat Pump Training',
    description: 'Profile-enabled heat pump simulation with trainer-defined guidance and fixed values',
  },
  {
    path: '/simulations/heat-pump-training-preview',
    element: <HeatPumpSimulation trainingProfileMode trainingProfileAccessMode="preview" />,
    name: 'Heat Pump Training Preview',
    description: 'Authenticated preview route for testing draft and published training profiles in the simulation',
    requiresAuth: true,
  },
  {
    path: '/simulations/hydraulic-balancing',
    element: <HydraulicBalancingSimulation />,
    name: 'Hydraulic Balancing',
    description: 'Multi-room hydraulic balancing simulation for heat pump systems',
  },
  {
    path: '/simulations/hydraulic-balancing-testing',
    element: <HydraulicBalancingTestingSimulation />,
    name: 'Hydraulic Balancing Testing',
    description: 'Next-generation educational hydraulic balancing simulation (testing)',
  },
  {
    path: '/simulations/hybrid-system-integration',
    element: <HybridSystemIntegrationTool />,
    name: 'Hybrid System Integration Tool',
    description: 'Draft animated builder for hybrid heat pump systems, source dispatch, cost, and carbon analysis',
  },
  {
    path: '/training-curriculum',
    element: <TrainingCurriculumIntro />,
    name: 'Training Curriculum',
    description: 'Public heat pump training curriculum overview and learning entry point',
    menuGroup: MenuGroups.PUBLIC,
    menuKey: 'trainingCurriculum',
  },
  // Public Events (no auth required)
  {
    path: '/events',
    element: <EventsPublic />,
    name: 'Events',
    description: 'Browse upcoming events and training sessions',
    menuGroup: MenuGroups.PUBLIC,
    menuKey: 'events',
  },
  {
    path: '/events/:id',
    element: <EventDetail />,
    name: 'Event Details',
    description: 'View event details and register',
  },
  {
    path: '/events/:id/register',
    element: <EventRegistration />,
    name: 'Event Registration',
    description: 'Register for an event',
  },
  // Approval preview routes (logged-in users only)
  {
    path: '/navigation-assistant',
    element: <NavigationAssistantReview />,
    name: 'Navigation Assistant',
    description: 'Preview the navigation assistant before it is released into the shared header experience',
    requiresAuth: true,
    menuGroup: MenuGroups.APPROVAL,
    menuKey: 'navigationAssistant',
  },
  // Learning Packages public pages — in the approval group for verification.
  // TODO: on release, remove requiresAuth and change menuGroup to MenuGroups.PUBLIC.
  {
    path: '/packages',
    element: <PackagesPublicList />,
    name: 'Learning Packages',
    description: 'Browse published learning packages',
    requiresAuth: true,
    menuGroup: MenuGroups.APPROVAL,
    menuKey: 'learningPackagesPublic',
  },
  {
    path: '/packages/:id',
    element: <PackagePublicDetail />,
    name: 'Learning Package',
    description: 'Explore a learning package and track your personal progress',
    requiresAuth: true,
  },
  {
    path: '/news',
    element: <NewsPublic />,
    name: 'News',
    description: 'Browse HeatCraftHP news and project updates',
    menuGroup: MenuGroups.PUBLIC,
    menuKey: 'news',
  },
  {
    path: '/news/:id/:slug',
    element: <NewsDetail />,
    name: 'News Details',
    description: 'Read a News article',
  },
  {
    path: '/news/:id',
    element: <NewsDetail />,
    name: 'News Details',
    description: 'Read a News article',
  },
  // Account routes (any authenticated user)
  {
    path: '/profile',
    element: <Profile />,
    name: 'Profile',
    description: 'User profile page for managing account settings',
    requiresAuth: true,
    menuGroup: MenuGroups.ACCOUNT,
    menuKey: 'profile',
  },
  // Survey Routes (Content Creator or Admin)
  {
    path: '/surveys',
    element: <SurveysList />,
    name: 'Surveys',
    description: 'Content creator page for managing surveys',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
    menuGroup: MenuGroups.CONTENT,
    menuKey: 'surveys',
  },
  {
    path: '/surveys/new',
    element: <SurveyEditor />,
    name: 'Create Survey',
    description: 'Create a new survey',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/surveys/:id',
    element: <SurveyEditor />,
    name: 'Edit Survey',
    description: 'Edit an existing survey',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/surveys/:id/statistics',
    element: <SurveyStatistics />,
    name: 'Survey Statistics',
    description: 'View survey statistics and analytics',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/surveys/:id/responses',
    element: <SurveyResponses />,
    name: 'Survey Responses',
    description: 'View individual participant responses',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  // Quiz Collections Routes (Content Creator or Admin)
  {
    path: '/quiz-collections',
    element: <QuizCollectionsList />,
    name: 'Quiz Collections',
    description: 'Content creator page for managing quiz collections',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
    menuGroup: MenuGroups.CONTENT,
    menuKey: 'quizCollections',
  },
  {
    path: '/quiz-collections/:id',
    element: <QuizCollectionEditor />,
    name: 'Edit Quiz Collection',
    description: 'Edit a quiz collection with topics and questions',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/quiz-collections/:id/statistics',
    element: <QuizStatistics />,
    name: 'Quiz Statistics',
    description: 'View aggregate statistics and per-question accuracy for a quiz collection',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/quiz-collections/:id/attempts',
    element: <QuizAttempts />,
    name: 'Quiz Attempts',
    description: 'Browse and manage individual quiz attempts',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  // Learning Path Routes (Content Creator or Admin)
  {
    path: '/learning-paths',
    element: <LearningPathsList />,
    name: 'Learning Paths',
    description: 'Create and manage visual A4 learning paths',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
    menuGroup: MenuGroups.CONTENT,
    menuKey: 'learningPaths',
  },
  {
    path: '/learning-paths/new',
    element: <LearningPathEditor />,
    name: 'Create Learning Path',
    description: 'Create a visual A4 learning path',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/learning-paths/:id',
    element: <LearningPathEditor />,
    name: 'Edit Learning Path',
    description: 'Edit a visual A4 learning path',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/learning-paths/:id/statistics',
    element: <LearningPathStatistics />,
    name: 'Learning Path Statistics',
    description: 'View learning path visits and click statistics',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  // Learning Package Routes (Content Creator or Admin)
  {
    path: '/learning-packages',
    element: <LearningPackagesList />,
    name: 'Learning Packages',
    description: 'Bundle learning paths, quizzes, and training profiles into publishable packages',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
    menuGroup: MenuGroups.CONTENT,
    menuKey: 'learningPackages',
  },
  {
    path: '/learning-packages/roster',
    element: <AnonymousRosterPage />,
    name: 'Anonymous Key Roster',
    description: 'Manage display names for anonymous visitor keys',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/learning-packages/new',
    element: <LearningPackageEditor />,
    name: 'Create Learning Package',
    description: 'Create a learning package',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/learning-packages/:id',
    element: <LearningPackageEditor />,
    name: 'Edit Learning Package',
    description: 'Edit a learning package',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/learning-packages/:id/statistics',
    element: <LearningPackageStatistics />,
    name: 'Learning Package Statistics',
    description: 'View learning package engagement, participants, and leaderboard',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  // Events Routes (Admin, Content Creator, Master Trainer)
  {
    path: '/admin/events',
    element: <EventsList />,
    name: 'Events',
    description: 'Manage events and training sessions',
    roles: [AppRoles.Admin, AppRoles.ContentCreator, AppRoles.MasterTrainer],
    menuGroup: MenuGroups.CONTENT,
    menuKey: 'manageEvents',
  },
  {
    path: '/admin/events/new',
    element: <EventEditor />,
    name: 'Create Event',
    description: 'Create a new event',
    roles: [AppRoles.Admin, AppRoles.ContentCreator, AppRoles.MasterTrainer],
  },
  {
    path: '/admin/events/:id',
    element: <EventEditor />,
    name: 'Edit Event',
    description: 'Edit an existing event',
    roles: [AppRoles.Admin, AppRoles.ContentCreator, AppRoles.MasterTrainer],
  },
  // News Routes (Admin supervises, assigned Content Creators create/own)
  {
    path: '/admin/news',
    element: <NewsList />,
    name: 'News',
    description: 'Manage multilingual News and creator assignments',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
    menuGroup: MenuGroups.CONTENT,
    menuKey: 'manageNews',
  },
  {
    path: '/admin/news/new',
    element: <NewsEditor />,
    name: 'Create News',
    description: 'Create a multilingual News item',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  {
    path: '/admin/news/:id',
    element: <NewsEditor />,
    name: 'Edit News',
    description: 'Edit an existing News item',
    roles: [AppRoles.Admin, AppRoles.ContentCreator],
  },
  // Training Profile Routes (Admin, Content Creator, Master Trainer)
  {
    path: '/training-profiles',
    element: <TrainingProfilesList />,
    name: 'Training Profiles',
    description: 'Manage heat pump simulation training profiles',
    roles: [AppRoles.Admin, AppRoles.ContentCreator, AppRoles.MasterTrainer],
    menuGroup: MenuGroups.TRAINING,
    menuKey: 'trainingProfiles',
  },
  {
    path: '/training-profiles/new',
    element: <TrainingProfileEditor />,
    name: 'Create Training Profile',
    description: 'Create a heat pump simulation training profile',
    roles: [AppRoles.Admin, AppRoles.ContentCreator, AppRoles.MasterTrainer],
  },
  {
    path: '/training-profiles/:id',
    element: <TrainingProfileEditor />,
    name: 'Edit Training Profile',
    description: 'Edit a heat pump simulation training profile',
    roles: [AppRoles.Admin, AppRoles.ContentCreator, AppRoles.MasterTrainer],
  },
  // Admin Routes
  {
    path: '/admin/users',
    element: <AdminUsers />,
    name: 'Users',
    description: 'Admin page for managing user accounts',
    roles: [AppRoles.Admin],
    menuGroup: MenuGroups.ADMIN,
    menuKey: 'users',
  },
  {
    path: '/admin/countries',
    element: <AdminCountries />,
    name: 'Countries',
    description: 'Admin page for managing country metadata',
    roles: [AppRoles.Admin],
    menuGroup: MenuGroups.ADMIN,
    menuKey: 'countries',
  },
  {
    path: '/admin/training-curriculum',
    element: <TrainingCurriculumAdmin />,
    name: 'Training Curriculum Assignments',
    description: 'Admin page for assigning fixed curriculum nodes to published resources by country',
    roles: [AppRoles.Admin],
    menuGroup: MenuGroups.ADMIN,
    menuKey: 'trainingCurriculumAssignments',
  },
  {
    path: '/admin/levels-of-difficulty',
    element: <AdminLevelsOfDifficulty />,
    name: 'Levels of Difficulty',
    description: 'Admin page for managing difficulty level metadata',
    roles: [AppRoles.Admin],
    menuGroup: MenuGroups.ADMIN,
    menuKey: 'levelsOfDifficulty',
  },
  {
    path: '/admin/exception-logs',
    element: <AdminExceptionLogs />,
    name: 'Exception Logs',
    description: 'Admin page for viewing and managing system logs and exceptions',
    roles: [AppRoles.Admin],
    menuGroup: MenuGroups.ADMIN,
    menuKey: 'exceptionLogs',
  },
  {
    path: '/admin/deleted-files',
    element: <AdminDeletedFiles />,
    name: 'Deleted Files',
    description: 'Admin page for viewing file deletion audit logs',
    roles: [AppRoles.Admin],
    menuGroup: MenuGroups.ADMIN,
    menuKey: 'deletedFiles',
  },
  {
    path: '/admin/file-storage',
    element: <AdminFileStorage />,
    name: 'File Storage',
    description: 'Admin page for managing files, storage health, and resolving file integrity issues',
    roles: [AppRoles.Admin],
    menuGroup: MenuGroups.ADMIN,
    menuKey: 'fileStorage',
  },
  {
    path: '/admin/data-deletion-logs',
    element: <AdminDataDeletionLogs />,
    name: 'Data Deletion Logs',
    description: 'Admin page for viewing data deletion audit trail across the system',
    roles: [AppRoles.Admin],
    menuGroup: MenuGroups.ADMIN,
    menuKey: 'dataDeletionLogs',
  },
  // Public Survey Participation (no auth required)
  {
    path: '/survey/:id',
    element: <SurveyParticipate />,
    name: 'Survey',
    description: 'Public survey participation page',
  },
  // Public Quiz Play (no auth required)
  {
    path: '/quiz/:id',
    element: <QuizPlay />,
    name: 'Quiz',
    description: 'Public quiz play page',
  },
  // Public Learning Path Play (no auth required)
  {
    path: '/learning-path/:id',
    element: <LearningPathPlay />,
    name: 'Learning Path',
    description: 'Public learning path play page',
  },
  // 404 catch-all
  {
    path: '*',
    element: <NotFound />,
    name: 'Not Found',
    description: '404 error page for non-existent routes',
  }
];

/**
 * Helper to wrap element with ProtectedRoute if needed
 */
const wrapWithProtection = (route) => {
  const { element, roles, requiresAuth } = route;
  
  // Route requires specific roles
  if (roles && roles.length > 0) {
    return (
      <ProtectedRoute allowedRoles={roles}>
        {element}
      </ProtectedRoute>
    );
  }
  
  // Route requires authentication but no specific role
  if (requiresAuth) {
    return (
      <ProtectedRoute>
        {element}
      </ProtectedRoute>
    );
  }
  
  // Public route
  return element;
};

/**
 * RoutesList component that renders all configured application routes
 * Iterates through the routes array and generates Route components
 * Automatically wraps protected routes with ProtectedRoute
 * @returns {JSX.Element} Routes component containing all application routes
 */
export const RoutesList = () => {
  return (
    <Routes>
      {routes.map((route) => (
        <Route 
          key={route.path} 
          path={route.path} 
          element={wrapWithProtection(route)} 
        />
      ))}
    </Routes>
  );
};

export default RoutesList;
